import { ApiFilePostStatusEnum, type ApiFilePostRequest } from '../service/apis/FileApi';
import { pickLatestVersion } from './fileDetailVersionPolicy';
import { fileNameWithoutExtension } from './stringHelpers';
import { getExtensionFromUrl } from './downloadFromLinks';
import { resolveMimeType } from './fileMimeType';
import { resolveUploadThumbnail } from './uploadThumbnail';

export type AddFileAsVersionDeps = {
  getFileById: (id: number) => Promise<{ success?: boolean; data?: any; message?: string }>;
  createFile: (data: ApiFilePostRequest) => Promise<{ success?: boolean; message?: string }>;
  deleteEntry?: (entries: Array<{ id: number; entryType: string }>) => Promise<unknown>;
};

export type AddFileAsVersionResult =
  | { success: true }
  | { success: false; message?: string; code?: 'MULTIPLE_VERSIONS' };

export async function addFileEntryAsVersion(
  sourceFileId: number,
  targetFile: { id: number; name: string },
  projectId: number,
  deps: AddFileAsVersionDeps,
  options?: { removeSource?: boolean }
): Promise<AddFileAsVersionResult> {
  const sourceRes = await deps.getFileById(sourceFileId);
  if (!sourceRes?.success || !sourceRes.data?.versions?.length) {
    return { success: false, message: 'Source file could not be loaded.' };
  }

  if (sourceRes.data.versions.length > 1) {
    return { success: false, code: 'MULTIPLE_VERSIONS' };
  }

  const latest = pickLatestVersion(sourceRes.data.versions);
  const fileUrl = latest?.fileUrl || latest?.url || latest?.downloadUrl || latest?.path;
  if (!fileUrl || typeof fileUrl !== 'string') {
    return { success: false, message: 'Source file has no downloadable content.' };
  }

  const sourceName = String(sourceRes.data.name || 'file');
  let uploadFileName = sourceName;
  if (!uploadFileName.includes('.')) {
    const ext = getExtensionFromUrl(fileUrl);
    if (ext) uploadFileName = `${uploadFileName}.${ext}`;
  }

  let blob: Blob;
  try {
    const response = await fetch(fileUrl, { credentials: 'omit' });
    if (!response.ok) {
      return { success: false, message: 'Failed to fetch source file content.' };
    }
    blob = await response.blob();
  } catch {
    return { success: false, message: 'Failed to fetch source file content.' };
  }

  const file = new File([blob], uploadFileName, {
    type: resolveMimeType(uploadFileName, blob.type),
  });

  const thumbnail = (await resolveUploadThumbnail(file)) ?? undefined;

  const createRes = await deps.createFile({
    name: fileNameWithoutExtension(targetFile.name),
    file,
    projectId,
    fileId: targetFile.id,
    status: ApiFilePostStatusEnum.NeedsReview,
    share: [],
    ...(thumbnail ? { thumbnail } : {}),
  });

  if (!createRes?.success) {
    return { success: false, message: createRes?.message ?? 'Failed to add version.' };
  }

  if (options?.removeSource && deps.deleteEntry) {
    await deps.deleteEntry([{ id: sourceFileId, entryType: 'File' }]);
  }

  return { success: true };
}
